import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "./ManageAgentSubscription.scss";
import { Box, Modal } from "@mui/material";
import { SubscriptionApi } from "../../../utils/api/SubscriptionApi";
import { clientRoutes } from "../../../utils/routes";
import BackArrowIcon from "../../../components/icons/BackArrowIcon";
import TrueRoundIcon from "../../../components/icons/TrueRoundIcon";
import DownArrowIcon from "../../../components/icons/DownArrowIcon";
import ProtectedComponent from "../../../components/ProtectedComponent";
import NavigationLayout from "../navigation";

const ManageAgentSubscription: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const historyId = searchParams.get("subscrpition_history_id");

  const [data, setData] = useState<any>(
    location.state?.subscriptionData || null,
  );
  const [loading, setLoading] = useState<boolean>(
    !location.state?.subscriptionData,
  );

  const [openFeatureIndex, setOpenFeatureIndex] = useState<number | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!data && historyId) {
        try {
          setLoading(true);
          const res: any = await SubscriptionApi.getMySubscriptionHistory({
            id: historyId,
            subscription_status: "active",
          });

          if (res?.data?.success && res?.data?.data) {
            const resultData = Array.isArray(res.data.data)
              ? res.data.data[0]
              : res.data.data;
            setData(resultData);
          }
        } catch (error) {
          console.error("Failed to fetch subscription details", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [historyId, data]);

  const handleBack = () => {
    navigate(clientRoutes.manageSubscriptions);
  };

  const toggleFeature = (index: number) => {
    setOpenFeatureIndex(openFeatureIndex === index ? null : index);
  };

  const getPlanName = () => {
    return (
      data?.subscription_plan?.plan_name ||
      data?.subscription_id?.plan_name ||
      "Standard Plan"
    );
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "N/A";
    const date = dayjs(dateString);
    if (date.isValid()) {
      return date.format("MMMM D, YYYY");
    }
    const customDate = dayjs(dateString, "DD-MM-YYYY");
    if (customDate.isValid()) {
      return customDate.format("MMMM D, YYYY");
    }
    return "N/A";
  };

  const getPlanFeatures = () => {
    return (
      data?.subscription_plan?.plan_features ||
      data?.subscription_id?.plan_features ||
      []
    );
  };

  const getPriceDisplay = () => {
    let price = 0;
    const plan = data?.subscription_plan || data?.subscription_id;

    if (plan?.stripe_product_prices?.[0]) {
      const priceObj =
        plan.stripe_product_prices.find((p: any) => p.currency === "aud") ||
        plan.stripe_product_prices[0];
      price = priceObj.unit_amount / 100;
    } else if (plan?.price) {
      price = plan.price;
    }

    return `AUD ${price}`;
  };

  const getNextPaymentDate = () => {
    if (data?.subscription_end_date) {
      const date = dayjs(data.subscription_end_date, "DD-MM-YYYY");
      if (date.isValid()) {
        return date.format("MMMM DD, YYYY");
      }
    }
    return "N/A";
  };

  const getEndDateDisplay = () => {
    if (data?.subscription_end_date) {
      const date = dayjs(data.subscription_end_date, "DD-MM-YYYY");
      if (date.isValid()) {
        return date.format("MMM DD, YYYY");
      }
    }
    return "N/A";
  };

  const handleConfirmCancellation = async () => {
    try {
      setIsCancelModalOpen(false);
      setIsProcessing(true);

      const stripeSubscriptionId = data?.stripe_subscription_id;

      if (!stripeSubscriptionId) {
        throw new Error("Stripe subscription id not found");
      }

      const res = await SubscriptionApi.cancelStripeSubscription({
        stripe_subscription_id: stripeSubscriptionId,
      });

      if (res?.error) {
        throw new Error(res.error);
      }

      setIsProcessing(false);
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error("Cancel subscription error:", error);
      setIsProcessing(false);
      alert("Failed to cancel subscription. Please try again.");
    }
  };

  const handleDone = () => {
    setIsSuccessModalOpen(false);
    handleBack();
  };

  const planFeatures = getPlanFeatures();

  if (loading)
    return (
      <div className="p-4" style={{ padding: "20px" }}>
        Loading subscription details...
      </div>
    );
  if (!data && !loading)
    return (
      <div className="p-4" style={{ padding: "20px" }}>
        Subscription not found.
      </div>
    );

  return (
    <>
      <ProtectedComponent>
        <NavigationLayout>
          <div className="manage-agent-subscription-container">
            <div className="agent-view-back-btn" onClick={handleBack}>
              <div className="back-icon">
                <BackArrowIcon />
              </div>
              <span className="bakc-text-contain">Back</span>
            </div>

            <div className="page-header">
              <p className="main-header">Manage subscription</p>
            </div>

            <div className="subscription-meta-data">
              <div className="meta-row">
                <div className="meta-item">
                  <label>Subscription id</label>
                  <p>{data?._id || "N/A"}</p>
                </div>
                <div className="meta-item">
                  <label>Stripe subscription id</label>
                  <p>{data?.stripe_subscription_id || "N/A"}</p>
                </div>
                <div className="meta-item">
                  <label>Status</label>
                  <div className="status-badge-green">Active</div>
                </div>
              </div>
              <div className="meta-row second-row">
                <div className="meta-item">
                  <label>Start date</label>
                  <p>
                    {formatDate(
                      data?.subscription_start_date || data?.created_at,
                    )}
                  </p>
                </div>
                <div className="meta-item">
                  <label>Renewal date</label>
                  <p>{formatDate(data?.subscription_end_date)}</p>
                </div>
              </div>
            </div>

            <div className="section-subtitle">
              Agent marketing subscription -{" "}
              {data?.listing_type === "sale" ? "For Sale" : "For Rent"}
            </div>

            <div className="details-card">
              <div className="card-top-row">
                <div className="card-info">
                  <h3 className="plan-name">{getPlanName()}</h3>
                  <p className="plan-type-text">Agent marketing</p>
                </div>
                <div className="card-status">
                  <span className="status-dot"></span>
                  <span className="status-text">Active</span>
                </div>
              </div>

              <div className="card-price-row">
                <p className="price-text">
                  {getPriceDisplay()}{" "}
                  <span className="per-month">
                    per <br /> month
                  </span>
                </p>
              </div>

              <div className="card-payment-info">
                <p>Next Payment: {getNextPaymentDate()}</p>
              </div>
            </div>

            <div className="features-section">
              <p className="features-main-title">Plan features</p>

              <div className="features-grid">
                {planFeatures.length > 0 ? (
                  planFeatures.map((feature: any, index: number) => (
                    <div
                      className="feature-item-wrapper"
                      key={feature._id || index}
                    >
                      <div
                        className="feature-header"
                        onClick={() => toggleFeature(index)}
                      >
                        <div className="feature-title-group">
                          <span className="check-icon">
                            <TrueRoundIcon />
                          </span>
                          <span className="feature-title">{feature.title}</span>
                        </div>
                        <div
                          className={`arrow-icon ${
                            openFeatureIndex === index ? "rotate" : ""
                          }`}
                        >
                          <DownArrowIcon />
                        </div>
                      </div>

                      <div
                        className={`feature-description ${
                          openFeatureIndex === index ? "expanded" : ""
                        }`}
                      >
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p
                    style={{
                      color: "#666",
                      fontFamily: "Inter",
                      fontSize: "14px",
                    }}
                  >
                    No features listed for this plan.
                  </p>
                )}
              </div>
            </div>

            <div className="action-footer">
              <button
                className="cancel-sub-btn"
                onClick={() => setIsCancelModalOpen(true)}
              >
                Cancel subscription
              </button>
            </div>
          </div>

          <Modal
            open={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            className="agent-cancel-modal-overlay"
          >
            <Box className="agent-cancel-modal-content">
              <p className="cancel-modal-title">
                Are you sure you want to cancel the Agent Marketing Subscription
                for {data?.for_sale ? "Sale" : "Rent"}?
              </p>
              <p className="cancel-modal-subtitle">
                Your plan remains active with full features until{" "}
                {getEndDateDisplay()}.
              </p>
              <div className="cancel-modal-actions">
                <button
                  className="btn-modal-cancel"
                  onClick={() => setIsCancelModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-modal-confirm"
                  onClick={handleConfirmCancellation}
                >
                  Confirm cancellation
                </button>
              </div>
            </Box>
          </Modal>

          <Modal
            open={isProcessing}
            className="agent-cancel-modal-overlay"
            onClose={() => {}}
          >
            <Box className="agent-cancel-modal-content processing-content">
              <div className="processing-spinner"></div>
              <p className="processing-text">
                Processing your request <br /> Please wait...
              </p>
            </Box>
          </Modal>

          <Modal
            open={isSuccessModalOpen}
            onClose={handleDone}
            className="agent-cancel-modal-overlay"
          >
            <Box className="agent-cancel-modal-content success-content">
              <div className="success-icon-container">
                <div className="green-circle">
                  <div className="tick"></div>
                </div>
              </div>

              <p className="success-cancel-modal-title">
                Your Agent Marketing Subscription has been cancelled.
              </p>

              <p className="success-cancel-modal-subtitle">
                Your plan remains active with full features until{" "}
                {getEndDateDisplay()}.
              </p>

              <button className="btn-modal-done" onClick={handleDone}>
                Done
              </button>
            </Box>
          </Modal>
        </NavigationLayout>
      </ProtectedComponent>
    </>
  );
};

export default ManageAgentSubscription;
